import { AxiosError, isAxiosError, isCancel } from "axios";

/**
 * Statuses where the request reached a proxy but never a healthy backend, so
 * sending the same request again is worth doing.
 */
const RETRIABLE_HTTP_STATUSES = new Set([502, 503, 504]);

/**
 * True when a request failed without ever getting an answer from the backend.
 *
 * The motivating case is BankID on iOS: when the BankID app takes foreground the
 * web page is suspended and any in-flight XHR dies, which axios reports as HTTP
 * status 0 with `code: "ERR_NETWORK"` and no `response`. That failure says
 * nothing about the state of the operation on the server, so treating it as
 * final throws away work that is still progressing.
 *
 * Cancellations are excluded: an aborted signal or `queryClient.cancelQueries`
 * means the caller deliberately gave up, not that the network faltered.
 */
export const isTransientNetworkError = (error: unknown): boolean => {
  if (isCancel(error)) return false;

  if (error instanceof DOMException && error.name === "AbortError")
    return false;

  if (!isAxiosError(error)) return false;

  // `isCancel` only recognises axios' own CanceledError (it keys off `__CANCEL__`),
  // so check the code too — retrying a request the caller cancelled is worse than
  // failing it.
  if (error.code === AxiosError.ERR_CANCELED) return false;

  if (!error.response) return true;

  return RETRIABLE_HTTP_STATUSES.has(error.response.status);
};
